import { axiosInstance } from "../axios-Instance";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../react-query/constants";
import { toast } from "react-toastify";
import { toastOptions } from "../utils";
import { getLoginToken } from "../storage";

async function getMetrics() {
  const data = await axiosInstance({
    url: "/metrics",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

async function getWitness() {
  const data = await axiosInstance({
    url: "/claim/witness/me?onlyPending=true",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

async function getPendingValidation() {
  const data = await axiosInstance({
    url: "/claim/payment?onlyPending=true",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

async function getPaid() {
  const data = await axiosInstance({
    url: "/claim/paid?onlyPending=true",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

async function getCompleted() {
  const data = await axiosInstance({
    url: "/claim/completed?onlyPending=true",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

async function getLogs() {
  const data = await axiosInstance({
    url: "/audit",
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });

  return data?.data?.data;
}

const claimAsset = async (formData: any) => {
  const data = await axiosInstance({
    url: `/claim/${formData?.id}/claim`,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

const addWitness = async (formData: any) => {
  const data = await axiosInstance({
    url: `/claim/${formData?.id}/witnesses`,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

const witnnessApprove = async (formData: any) => {
  const data = await axiosInstance({
    url: `/claim/${formData?.id}/witness/submit`,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

const confirmPayment = async (formData: any) => {
  const data = await axiosInstance({
    url: `/claim/${formData?.id}/payment/confirm`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

const generateReceipt = async (formData: any) => {
  const data = await axiosInstance({
    url: `/claim/${formData?.id}/receipt`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getLoginToken()}`,
    },
  });
  return data;
};

export function useClaimAsset() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => claimAsset(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.bid]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function useAddWitness() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => addWitness(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.pendingWitness]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function useWitnessApprove() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => witnnessApprove(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.pendingWitness]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function useConfirmPayment() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => confirmPayment(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.payment]);
      queryClient.invalidateQueries([queryKeys.paid]);
      queryClient.invalidateQueries([queryKeys.completed]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function useGenerateReceipt() {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, reset, isError, error } = useMutation({
    mutationFn: (formData) => generateReceipt(formData),
    onSuccess: (data) => {
      queryClient.invalidateQueries([queryKeys.paid]);
      queryClient.invalidateQueries([queryKeys.completed]);
    },
  });
  return { mutate, isSuccess, reset, isError, error };
}

export function usePendingWitness() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.pendingWitness],
    queryFn: () => getWitness(),
  });
  return data;
}

export function usePaymentValidation() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.payment],
    queryFn: () => getPendingValidation(),
  });
  return data;
}

export function usePaid() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.paid],
    queryFn: () => getPaid(),
  });
  return data;
}

export function useLogs() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.audit],
    queryFn: () => getLogs(),
  });
  return data;
}
export function useCompleted() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.completed],
    queryFn: () => getCompleted(),
  });
  return data;
}

export function useMetrics() {
  const fallback: any = [];
  const { data = fallback } = useQuery({
    queryKey: [queryKeys.metrics],
    queryFn: () => getMetrics(),
  });
  return data;
}
